import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";

/** One food item's tag changes: tags to add and tags to remove, by id. */
export interface FoodTagBatchDelta {
  food_id: number;
  add_tag_ids: number[];
  remove_tag_ids: number[];
}

export interface FoodTagBatchItemFailure {
  food_id: number;
  error: string;
}

interface FoodTagBatchOperationState {
  isApplying: boolean;
  completedCount: number;
  totalCount: number;
  failures: FoodTagBatchItemFailure[];
  error: string | null | undefined;
}

const initialState: FoodTagBatchOperationState = {
  isApplying: false,
  completedCount: 0,
  totalCount: 0,
  failures: [],
  error: null,
};

/**
 * Number of food items sent per request. Small enough that the progress bar
 * moves visibly on large runs (~1600 foods => ~64 requests), large enough to
 * keep the request count reasonable.
 */
const BATCH_CHUNK_SIZE = 25;

/**
 * Progress ticks dispatched by the apply thunk after each chunk, so the UI
 * can render "N / M items" while the run is still going.
 */
const batchProgressUpdated = createAction<{
  completedCount: number;
  failures: FoodTagBatchItemFailure[];
}>("foodTagBatchOperation/batchProgressUpdated");

/**
 * Apply tag add/remove deltas to many food items, in sequential chunks.
 * A chunk failure marks its items as failed and the run continues; aborting
 * (via the thunk's promise `.abort()`) stops cleanly between chunks, leaving
 * already-applied chunks in place.
 */
const applyFoodTagBatch = createAsyncThunk(
  "foodTagBatchOperation/applyFoodTagBatch",
  async (input: { deltas: FoodTagBatchDelta[] }, { dispatch, signal }) => {
    const failures: FoodTagBatchItemFailure[] = [];
    let completedCount = 0;
    for (
      let start = 0;
      start < input.deltas.length && !signal.aborted;
      start += BATCH_CHUNK_SIZE
    ) {
      const chunk = input.deltas.slice(start, start + BATCH_CHUNK_SIZE);
      try {
        // eslint-disable-next-line no-await-in-loop
        const results = await jspPlatformApi()
          .foodItem.patchFoodItemsTags({ body: { items: chunk } })
          .then((res) => res.data?.results ?? []);
        results
          .filter((result) => !result.success)
          .forEach((result) => {
            failures.push({
              food_id: result.food_id,
              error: result.error ?? "Unknown error!",
            });
          });
      } catch (reason) {
        let errMsg = "Unknown error!";
        const err = isApiError(reason) ? toJspApiError(reason) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else if (reason instanceof Error) {
          errMsg = reason.message;
        }
        chunk.forEach((delta) => {
          failures.push({ food_id: delta.food_id, error: errMsg });
        });
      }
      completedCount += chunk.length;
      dispatch(
        batchProgressUpdated({ completedCount, failures: [...failures] }),
      );
    }
    return { completedCount, failures };
  },
);

export const foodTagBatchOperationSlice = createSlice({
  name: "foodTagBatchOperation",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(applyFoodTagBatch.pending, (state, action) => {
      return {
        ...state,
        isApplying: true,
        completedCount: 0,
        totalCount: action.meta.arg.deltas.length,
        failures: [],
        error: null,
      };
    });
    builder.addCase(batchProgressUpdated, (state, action) => {
      return {
        ...state,
        completedCount: action.payload.completedCount,
        failures: action.payload.failures,
      };
    });
    builder.addCase(applyFoodTagBatch.fulfilled, (state, action) => {
      return {
        ...state,
        isApplying: false,
        completedCount: action.payload.completedCount,
        failures: action.payload.failures,
      };
    });
    builder.addCase(applyFoodTagBatch.rejected, (state, action) => {
      return {
        ...state,
        isApplying: false,
        // An abort is a user cancel, not an error
        error:
          action.error.name === "AbortError"
            ? null
            : ((action.payload as string | undefined) ?? action.error.message),
      };
    });
  },
});

export const foodTagBatchOperationActions = {
  applyFoodTagBatch,
};

const foodTagBatchOperationReducer = foodTagBatchOperationSlice.reducer;

export default foodTagBatchOperationReducer;
